import { useEffect, useState } from "react";

export type OAuthProviderId = "google" | "linkedin" | "github";

export type OAuthProvider = {
  id: OAuthProviderId | string;
  label: string;
  configured: boolean;
  authorize_path: string;
};

type Props = {
  disabled?: boolean;
};

function GoogleIcon() {
  return (
    <svg className="oauth-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg className="oauth-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="3" fill="#0A66C2" />
      <path
        fill="#fff"
        d="M7.1 9.4H4.6V19h2.5V9.4zM5.85 4.9c-.85 0-1.55.7-1.55 1.55S5 8 5.85 8 7.4 7.3 7.4 6.45 6.7 4.9 5.85 4.9zM19.4 12.7c0-2.1-1.2-3.5-3.4-3.5-1.1 0-1.9.5-2.3 1.1V9.4h-2.5c0 .5 0 9.6 0 9.6h2.5v-5.4c0-.3 0-.5.1-.7.2-.5.7-1 1.5-1 1.1 0 1.5.8 1.5 2v5.1H19.4v-5.6z"
      />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg className="oauth-btn-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#181717"
        d="M12 1.5C6.2 1.5 1.5 6.2 1.5 12c0 4.6 3 8.6 7.2 10 .5.1.7-.2.7-.5v-1.8c-2.9.6-3.5-1.2-3.5-1.2-.5-1.1-1.1-1.4-1.1-1.4-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.7.4-1.1.6-1.3-2.3-.3-4.8-1.2-4.8-5.2 0-1.1.4-2.1 1.1-2.8-.1-.3-.5-1.4.1-2.8 0 0 .9-.3 2.9 1.1.8-.2 1.7-.3 2.6-.3s1.8.1 2.6.3c2-.1 2.9-1.1 2.9-1.1.6 1.4.2 2.5.1 2.8.7.7 1.1 1.7 1.1 2.8 0 4-2.4 4.9-4.8 5.2.4.3.7.9.7 1.9v2.8c0 .3.2.6.7.5 4.2-1.4 7.2-5.4 7.2-10 0-5.8-4.7-10.5-10.5-10.5z"
      />
    </svg>
  );
}

function iconFor(id: string) {
  if (id === "google") return <GoogleIcon />;
  if (id === "linkedin") return <LinkedInIcon />;
  if (id === "github") return <GitHubIcon />;
  return null;
}

export function OAuthLoginButtons({ disabled }: Props) {
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/customers/oauth/providers", {
          headers: { Accept: "application/json" },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const list = Array.isArray(data.providers) ? (data.providers as OAuthProvider[]) : [];
        setProviders(list.filter((p) => p.configured));
      } catch {
        if (!cancelled) setProviders([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function start(provider: OAuthProvider) {
    setError(null);
    setBusy(provider.id);
    try {
      const res = await fetch(`${provider.authorize_path}?format=json`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.authorize_url) {
        throw new Error(data.error || `Could not start ${provider.label} login.`);
      }
      window.location.assign(String(data.authorize_url));
    } catch (err) {
      setBusy(null);
      setError(err instanceof Error ? err.message : "OAuth login failed.");
    }
  }

  if (!providers.length) return null;

  const google = providers.find((p) => p.id === "google");
  const secondary = providers.filter((p) => p.id === "linkedin" || p.id === "github");
  const extras = providers.filter(
    (p) => p.id !== "google" && p.id !== "linkedin" && p.id !== "github",
  );

  return (
    <div className="oauth-login">
      <p className="oauth-login-divider">
        <span>Or continue with</span>
      </p>
      <div className="oauth-login-grid">
        {google ? (
          <button
            type="button"
            className="oauth-btn oauth-btn-google"
            disabled={disabled || busy !== null}
            onClick={() => void start(google)}
          >
            {iconFor("google")}
            <span>{busy === "google" ? "Redirecting…" : google.label}</span>
          </button>
        ) : null}
        {secondary.length > 0 ? (
          <div className="oauth-login-row">
            {secondary.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`oauth-btn oauth-btn-${p.id}`}
                disabled={disabled || busy !== null}
                onClick={() => void start(p)}
              >
                {iconFor(p.id)}
                <span>{busy === p.id ? "…" : p.label}</span>
              </button>
            ))}
          </div>
        ) : null}
        {extras.map((p) => (
          <button
            key={p.id}
            type="button"
            className="oauth-btn"
            disabled={disabled || busy !== null}
            onClick={() => void start(p)}
          >
            <span>{busy === p.id ? "Redirecting…" : p.label}</span>
          </button>
        ))}
      </div>
      {error ? (
        <p className="model-run-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
